// generic-input.component.ts
import { KeyValue } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  effect,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { InputLabelOptions, TypographyKeys } from '@finxone-platform/shared/sys-config-types';
import { Subject, takeUntil } from 'rxjs';
import { InputValidationFactory } from '../input-field-validation/input-validations-factory';
import { ValidationConfig } from '../input-field-validation/validation-config.type';

/**
 * A robust input component which provides enhanced form control capabilities with built-in validation and styling flexibility.
 * Find the usage guide here: https://dev.azure.com/finxone/Finxone%20Platform/_wiki/wikis/Finxone-Platform.wiki/353/Finxone-Input-Component-Usage-guide
 */

@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'finxone-textarea',
  templateUrl: './finxone-textarea.component.html',
  styleUrls: ['./finxone-textarea.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxoneTextAreaComponent),
      multi: true,
    },
  ],
})
export class FinxoneTextAreaComponent
  extends InputValidationFactory
  implements ControlValueAccessor, OnInit, OnChanges, AfterContentInit
{
  private readonly cd = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);

  @ViewChild('textAreaElementWrapper') inputElementWrapper: ElementRef;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @ContentChild('prefix', { static: false }) prefixContent: ElementRef;
  @Input() public type = 'text';
  @Input() public placeholder = '';
  @Input() public label = '';
  @Input() public inputMode = 'text';
  @Input() public class = '';
  @Input() public readOnly = false;
  @Input() public isDisabled = false;
  @Input() public formControlInvalid = false;
  @Input() public globalCssVariables: Record<string, string> = {};
  @Input() public customInputTemplate?: TemplateRef<any>;
  @Input() public customErrorTemplate?: TemplateRef<any>;
  @Input() public id = '';
  @Input() public name = '';
  @Input() public value = '';
  @Input() public infoText = '';
  @Input() public inputLabelTooltipText = '';

  // Validation Configuration
  @Input() validationConfig: ValidationConfig[] = [];
  @Input() showErrors = true;

  @Input() public disableAutocomplete = false;

  // Internal form control
  public control = new FormControl();
  @Input() parentControl?: FormControl;

  // Used for ControlValueAccessor
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  onTouched: Function = () => {};

  public labelType: InputLabelOptions;
  public labelTypeOptions = InputLabelOptions;
  public textStylingClass: TypographyKeys;
  public borderRadiusClass = '';
  public isInputOnFocus = false;
  private cssVars: Record<string, string> = {};
  public hasPrefixContent = false;
  public tooltipBorderRadiusClass = '';
  public tooltipTextStyleClass = '';

  constructor() {
    super();
    effect(() => {
      const globalCssVariables = this.configService.globalCssVariables();
      if (globalCssVariables) {
        this.cssVars = globalCssVariables;
        //get the label type and text style class from root styles
        this.labelType = this.getCssVariableValue('--inputFieldLabelType') as InputLabelOptions;
        this.textStylingClass = this.getCssVariableValue('--inputFieldTextStylingClass') as TypographyKeys;
        this.borderRadiusClass = this.getCssVariableValue('--inputFieldBorderRadiusClass');
        this.tooltipTextStyleClass = this.getCssVariableValue('--tooltipTextStyleClass');
        this.tooltipBorderRadiusClass = this.getCssVariableValue('--tooltipBorderRadiusClass');
      }
    });
  }

  ngOnInit() {
    if (this.parentControl) this.control = this.parentControl;
    this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cd.markForCheck();
    });
  }

  ngAfterContentInit() {
    // Check if the prefix content exists
    if (this.prefixContent) {
      this.hasPrefixContent = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']?.currentValue !== undefined) {
      this.control.setValue(changes['value']?.currentValue);
    }
    if (changes['validationConfig']?.currentValue !== undefined) {
      const validators = this.setupValidation(this.validationConfig);
      this.control.setValidators(validators);
      this.control.updateValueAndValidity();
    }
    if (changes['formControlInvalid']?.currentValue !== undefined) {
      this.setFormControlValidity(this.formControlInvalid);
    }
  }

  private setFormControlValidity(formControlInvalid: boolean) {
    if (!formControlInvalid) {
      this.control.markAsPristine();
      this.control.markAsUntouched();
    } else {
      this.control.markAsDirty();
      this.control.markAsTouched();
    }
  }

  public writeValue(value: string): void {
    this.value = value;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    isDisabled ? this.control.disable() : this.control.enable();
  }

  public onInputChange(event: Event) {
    const target = event.target as HTMLTextAreaElement;
    this.value = target.value;
    this.control.setValue(this.value);
    this.onChange(this.value);
    this.cd.markForCheck();
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }

  public getErrorMessageBasedOnKey(error: KeyValue<string, any>): string {
    const key = error.key;
    if (!this.validationConfig.some((config) => config.type === key) && typeof error.value === 'string') {
      return error.value;
    } else {
      const matchingConfig = this.validationConfig.find((config) => config.type === key);
      if (matchingConfig) {
        return this.getErrorMessage(matchingConfig);
      }
    }
    return 'Cannot find error message';
  }

  public handleLabelClick(): void {
    if (this.labelType === this.labelTypeOptions.FLOATING) {
      this.inputElementWrapper?.nativeElement.querySelector('textarea')?.focus();
    }
  }
}
